import { createContext, ReactElement, useCallback, useEffect, useReducer, useState } from 'react';
import { LOGIN, LOGOUT, SET_SELECTED_HOTEL } from 'store/actions';
import accountReducer from 'store/accountReducer';
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';
import { getAllowedHotels, setSelectedHotel as apiSetSelectedHotel } from 'api/r2m';
import type { HotelFromApi } from 'types/auth';
import { InitialLoginContextProps, JWTContextType } from 'types/auth';
import useConfig from 'hooks/useConfig';
import type { I18n } from 'types/config';

const SUPPORTED_LANGS: I18n[] = ['hu', 'en'];

function syncI18nFromUser(user: any, setField: (k: 'i18n', v: I18n) => void) {
  const pref = (user?.preferredLanguage ?? '').toLowerCase();
  if (pref && SUPPORTED_LANGS.includes(pref as I18n)) {
    setField('i18n', pref as I18n);
  }
}

const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  selectedHotelId: null
};

const SessionContext = createContext<JWTContextType | null>(null);

export function SessionAuthProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const [allowedHotels, setAllowedHotels] = useState<HotelFromApi[]>([]);
  const { setField } = useConfig();

  const loadAllowedHotels = useCallback(async (date?: Date) => {
    try {
      const list = await getAllowedHotels(date);
      setAllowedHotels(list);
    } catch {
      setAllowedHotels([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const response = await axios.get('/auth/me');
        const data = response.data;
        const user = data.user ?? data;
        const selectedHotelId = data.selectedHotelId ?? null;
        syncI18nFromUser(user, setField);
        dispatch({
          type: LOGIN,
          payload: { isLoggedIn: true, user, selectedHotelId }
        });
      } catch {
        dispatch({ type: LOGOUT });
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.isLoggedIn) {
      loadAllowedHotels();
    } else {
      setAllowedHotels([]);
    }
  }, [state.isLoggedIn, loadAllowedHotels]);

  const setSelectedHotel = useCallback(async (hotelId: number) => {
    await apiSetSelectedHotel(hotelId);
    dispatch({ type: SET_SELECTED_HOTEL, payload: { selectedHotelId: hotelId } });
  }, []);

  useEffect(() => {
    if (state.isLoggedIn && allowedHotels.length > 0 && !state.selectedHotelId) {
      setSelectedHotel(allowedHotels[0].id);
    }
  }, [state.isLoggedIn, state.selectedHotelId, allowedHotels, setSelectedHotel]);

  const login = async (email: string, password: string) => {
    try {
      await axios.post('/auth/login', { email, password });
      const response = await axios.get('/auth/me');
      const data = response.data;
      const user = data.user ?? data;
      const selectedHotelId = data.selectedHotelId ?? null;
      syncI18nFromUser(user, setField);
      dispatch({ type: LOGIN, payload: { isLoggedIn: true, user, selectedHotelId } });
    } catch (err: any) {
      const msg = err.response?.data?.message ?? err.response?.data?.errors?.[0]?.message ?? err.message ?? 'Login failed';
      throw new Error(msg);
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      await axios.post('/auth/register', { email, password, firstName, lastName });
      await login(email, password);
    } catch (err: any) {
      const msg = err.response?.data?.message ?? err.response?.data?.errors?.[0]?.message ?? err.message ?? 'Registration failed';
      throw new Error(msg);
    }
  };

  const logout = () => {
    axios.post('/auth/logout', {}).catch(() => {});
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (_email: string) => {};
  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  const contextValue: JWTContextType = {
    ...state,
    selectedHotelId: state.selectedHotelId ?? null,
    allowedHotels,
    setSelectedHotel,
    loadAllowedHotels,
    login,
    logout,
    register,
    resetPassword,
    updateProfile
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
}

export default SessionContext;
